import bzxApi, { IApiAirdrop } from 'app-lib/apis/bzxApi'
import providerUtils from 'app-lib/providerUtils'
import { BigNumber } from '@0x/utils'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import { ChainId } from '../config/chains/chainCommon'

export enum Airdrop {
  BAL = 'BAL',
  BZRX_COMPENSATION1 = 'BZRX_COMPENSATION1',
  BZRX_COMPENSATION2 = 'BZRX_COMPENSATION2',
  BZRX_LOCKED = 'BZRX_LOCKED',
  P125_COMPENSATION1 = 'P125_COMPENSATION1',
}

/**
  ```
  airdrop: Airdrop
  airdropIndex: number
  amount: BigNumber
  index: BigNumber
  proof: string[]
  token: {
    symbol: string
    address: string
    decimals: number
  }
  userClaimed: boolean
  ```
  Note: `index` is the user index in the airdrop;
 */
export interface IAirdropInfo extends IApiAirdrop {
  airdrop: Airdrop
  airdropIndex: number
  userClaimed: boolean
}

function airdropPolygonCfg(index: number): {
  index: number
  airdropChainId: ChainId
  contract: string
} {
  return { index, airdropChainId: 137, contract: '0x59a6579C039F84A758665Ca416394BdF6A05985d' }
}

/**
 * Airdrops are identified via an index + chainId + contract address.
 * They all use the interface provided by MerkleDistributor.
 *
 * * BAL: is for users having staked BPT in our staking contract
 * * BZRX_COMPENSATION: is for the november 2021 hack
 * * P125_COMPENSATION1: is for the november 2021 hack; first airdrop of debt token
 */
export const airdrops: Record<
  Airdrop,
  { index: number; airdropChainId: ChainId; contract: string }
> = {
  BAL: {
    index: 0,
    airdropChainId: 1,
    contract: '0xbAa522162728e3F31E1837E4cbd81687D309767B',
  },
  BZRX_COMPENSATION1: {
    index: 0,
    airdropChainId: 137,
    contract: '0xFA079100e297253a1eb9783aa93646ecF2d5615e',
  },
  P125_COMPENSATION1: airdropPolygonCfg(1),
  BZRX_COMPENSATION2: airdropPolygonCfg(2),
  BZRX_LOCKED: airdropPolygonCfg(Infinity), // this airdrop ID is actually used for multiple airdrops
}

export default class AirdropService {
  public fulcrumProvider: FulcrumProvider

  getDependenciesStrict(airdrop: Airdrop) {
    const deps = providerUtils.getDependenciesStrict(this.fulcrumProvider)
    const airdropInfo = airdrops[airdrop]
    const merkle = deps.contractsSource.getMerkelDistributorContract(airdropInfo.contract)
    return { ...deps, merkle }
  }

  getLocalAirdropId(chainId: ChainId, airdropIndex: number, address: string) {
    return `airdrop-${chainId}-${airdropIndex}-${address}`
  }

  /**
   * Set a flag in localstorage to avoid rechecking a claimed airdrop
   */
  setChecked(chainId: ChainId, airdropIndex: number, address: string) {
    localStorage.setItem(this.getLocalAirdropId(chainId, airdropIndex, address), 'true')
  }

  /**
   * True if the user has already checked on the current device if he's entitled to an airdrop.
   * This is to avoid checking for airdrop everytime the user user visits the page.
   */
  wasChecked(chainId: ChainId, airdropIndex: number, address: string) {
    return !!localStorage.getItem(this.getLocalAirdropId(chainId, airdropIndex, address))
  }

  /**
   * This checks in our backend API if the user is entitled to an airdrop.
   * We store the list of airdrops in our DB using merkle tree, not onchain.
   */
  async getUserAirdropInfo(
    chainId: ChainId,
    airdropIndex: number,
    address: string
  ): Promise<IApiAirdrop> {
    const res = await bzxApi.getUserAirdropInfo(chainId, airdropIndex, address)
    res.amount = res.amount.div(10 ** res.token.decimals)
    return res
  }

  /**
   * Check onChain if the airdrop was claimed
   */
  async isClaimed(airdrop: Airdrop, userIndex: number) {
    const { contractsSource } = providerUtils.getDependenciesStrictRead(this.fulcrumProvider)
    const airdropInfo = airdrops[airdrop]
    const merkle = contractsSource.getMerkelDistributorContract(airdropInfo.contract)
    return merkle.isClaimed(new BigNumber(airdropInfo.index), new BigNumber(userIndex)).callAsync()
  }

  /**
   * Checks in our api for any bzrx locked airdrop and check if it was claimed onchain
   */
  async checkBzrxLockedAirdrops(address: string): Promise<IAirdropInfo[]> {
    const { contractsSource } = providerUtils.getDependenciesStrictRead(this.fulcrumProvider)
    const airdropsFromApi = await bzxApi.getBzrxLockedAirdrops(address)
    const merkle = contractsSource.getMerkelDistributorContract(airdrops.BZRX_LOCKED.contract)
    const userAirdrops: IAirdropInfo[] = []
    for (const airdrop of airdropsFromApi) {
      const userClaimed = await merkle
        .isClaimed(new BigNumber(airdrop.airdropIndex), new BigNumber(airdrop.index))
        .callAsync()
      userAirdrops.push({ ...airdrop, userClaimed, airdrop: Airdrop.BZRX_LOCKED })
    }
    return userAirdrops
  }

  /**
   * Check if the user has an airdrop and if it was claimed.
   * Sets a localstorage flag to avoid checking again if user has no airdrop or already claimed.
   * Returns the airdrop info.
   */
  async checkUserAirdrop(airdrop: Airdrop, address: string): Promise<IAirdropInfo> {
    const { index, airdropChainId } = airdrops[airdrop]
    const userInfo = await this.getUserAirdropInfo(airdropChainId, index, address)
    const hasAirdrop = userInfo.amount.gt(0)
    let userClaimed = false
    if (hasAirdrop) {
      userClaimed = await this.isClaimed(airdrop, userInfo.index.toNumber())
    }
    if (!hasAirdrop || userClaimed) {
      this.setChecked(airdropChainId, index, address)
    }
    return { ...userInfo, ...{ airdrop, userClaimed, airdropIndex: index } }
  }

  /**
   * Note param merkleInfo.amount has to be already divided by its precision
   */
  claimAirdrop(airdropInfo: IAirdropInfo, address: string) {
    const { merkle } = this.getDependenciesStrict(airdropInfo.airdrop)
    const { index, amount, proof } = airdropInfo

    const tx = merkle.claim(
      new BigNumber(airdropInfo.airdropIndex),
      index,
      address,
      amount.times(10 ** 18),
      proof
    )

    return providerUtils.runTx(this.fulcrumProvider, tx, merkle)
  }

  constructor(fulcrumProvider: FulcrumProvider) {
    this.fulcrumProvider = fulcrumProvider
  }
}
